import * as React from 'react';
import { Button, InfoBox, styled, Typography } from '@beeline.com/bp-design-system';
import { ApolloError } from '@apollo/client';
import { ErrorAPIMessage } from '../../utils/APIError';
import { GraphQLError, GraphQLFormattedError } from 'graphql';

export interface ErrorProps {
  message?: ErrorAPIMessage;
  apolloError?: ApolloError | GraphQLError | GraphQLFormattedError<Record<string, any>>;
  onRetry?: (search?: any) => void;
  enableReload?: boolean;
}

interface LocalizableProps {
  localizedRetryText?: React.ReactNode;
  localizedHeaderText?: React.ReactNode;
  localizedReloadText?: React.ReactNode;
}

const ErrorMessage: React.FC<React.PropsWithChildren<ErrorProps & LocalizableProps>> = ({
  message,
  onRetry,
  enableReload = false,
  localizedRetryText = 'Retry',
  localizedHeaderText = 'Error',
  localizedReloadText = 'Reload',
  apolloError
}) => {
  const internalError = message?.internalError;
  const friendlyMessage = message?.friendlyMessage ?? apolloError?.message;
  const status = message?.status;
  return (
    <StyledWrapper>
      <InfoBox
        type="ERROR"
        title={localizedHeaderText}
        breakWords
        actions={
          !!onRetry || enableReload ? (
            <>
              {onRetry ? (
                <Button variant="contained" color="secondary" onClick={onRetry} dataTest="button">
                  {localizedRetryText}
                </Button>
              ) : null}

              {enableReload ? (
                <Button
                  variant="contained"
                  dataTest="button"
                  color="secondary"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {localizedReloadText}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      >
        <div className="error__container--outer">
          <div className="error__container--inner">
            <div className="error__message">
              <Typography variant="body" large>
                {friendlyMessage}
              </Typography>
              {internalError ? <pre className="error__internal-message">{internalError.message}</pre> : null}
            </div>
            <Typography variant="body" large sx={{ mt: 3 }}>
              [{status} {internalError ? `- ${internalError.code}` : null}]
            </Typography>
          </div>
        </div>
      </InfoBox>
    </StyledWrapper>
  );
};
export default ErrorMessage;

const StyledWrapper = styled('div')`
  display: flex;
  margin: 30px auto 0 auto;
  max-width: 30vw;

  .error__message {
    word-wrap: break-word;
  }
  .error__internal-message {
    white-space: normal;
  }
`;
